import React, { useEffect, useState } from 'react';
import { useUser, useClerk, SignInButton } from '@clerk/clerk-react';
import { identifyUser, trackSignedIn, trackPurchaseStarted } from '../utils/analytics';
import { syncUser, getUserProfile } from '../utils/api';
import { useTranslation } from '../i18n/index.jsx';
import { useTheme } from '../contexts/ThemeContext.jsx';
import './AuthButton.css';

export default function AuthButton({ open, onToggle, onClose }) {
  const { isSignedIn, user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const { t } = useTranslation();
  const { updateProStatus } = useTheme();
  const [profile, setProfile] = useState(null); // { is_pro, theme_purchases, games_played, games_won }

  useEffect(() => {
    if (isSignedIn && user) {
      // Identify in analytics
      identifyUser(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
      });
      const method = user.primaryEmailAddress?.emailAddress?.includes('privaterelay.appleid.com') ? 'apple' : 'google';
      trackSignedIn(method);

      // Sync with backend & load profile
      const email = user.primaryEmailAddress?.emailAddress || '';
      const name = user.fullName || '';

      syncUser(user.id, email, name).then(() => {
        getUserProfile(user.id).then(data => {
          if (data) {
            setProfile(data);
            updateProStatus(data.is_pro, data.theme_purchases || []);
          }
        });
      });
    }
  }, [isSignedIn, user]);

  if (!isLoaded) return null;

  if (!isSignedIn) {
    return (
      <div className="auth-area">
        <SignInButton mode="modal">
          <button className="sign-in-btn">{t('signIn')}</button>
        </SignInButton>
      </div>
    );
  }

  const isPro = profile?.is_pro || false;
  const gamesPlayed = profile?.games_played || 0;
  const gamesWon = profile?.games_won || 0;

  return (
    <div className="auth-area">
      <button className="avatar-btn" onClick={onToggle}>
        {user.imageUrl ? (
          <img src={user.imageUrl} alt="" className="avatar-img" />
        ) : (
          <span className="avatar-letter">{user.firstName?.[0] || '?'}</span>
        )}
        {isPro && <span className="pro-dot" />}
      </button>

      {open && (
        <>
          <div className="profile-backdrop" onClick={onClose} />
          <div className="profile-panel">
            <div className="profile-header">
              {user.imageUrl && <img src={user.imageUrl} alt="" className="profile-avatar" />}
              <div className="profile-info">
                <div className="profile-name-row">
                  <span className="profile-name">{user.fullName || user.firstName || 'Player'}</span>
                  <span className={`profile-badge ${isPro ? 'pro' : 'free'}`}>
                    {isPro ? t('pro') : t('free')}
                  </span>
                </div>
                {user.primaryEmailAddress?.emailAddress && (
                  <div className="profile-email-row">
                    <span className="profile-email-text">{user.primaryEmailAddress.emailAddress}</span>
                    <button
                      className="profile-copy-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(user.primaryEmailAddress.emailAddress);
                        const el = document.querySelector('.profile-copy-btn');
                        if (el) { el.textContent = 'Copied!'; setTimeout(() => { el.textContent = 'Copy'; }, 1500); }
                      }}
                    >Copy</button>
                  </div>
                )}
              </div>
            </div>

            {gamesPlayed > 0 && (
              <div className="profile-stats">
                <div className="stat">
                  <span className="stat-value">{gamesPlayed}</span>
                  <span className="stat-label">{t('played')}</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{gamesWon}</span>
                  <span className="stat-label">{t('won')}</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{gamesPlayed > 0 ? Math.round((gamesWon / gamesPlayed) * 100) : 0}%</span>
                  <span className="stat-label">{t('rate')}</span>
                </div>
              </div>
            )}

            {!isPro && (
              <div className="upgrade-section">
                <p className="upgrade-text">{t('unlockAll')}</p>
                <button className="upgrade-btn" onClick={() => {
                  trackPurchaseStarted('pro');
                  const stripeLink = import.meta.env.VITE_STRIPE_PRO_LINK;
                  if (stripeLink) {
                    // Pass clerk_id so Stripe webhook can link payment to user
                    const url = `${stripeLink}?client_reference_id=${user.id}`;
                    window.open(url, '_blank');
                  } else {
                    alert('Payment not configured yet');
                  }
                }}>
                  {t('upgradeTo')}
                </button>
              </div>
            )}

            <div className="profile-footer">
              <button className="sign-out-btn" onClick={() => { onClose(); signOut(); }}>
                {t('signOut')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
